import {awaitPriorsLoaded, choose, logDebug, observerOptions, recordExposure} from "@version.compare/bandit";
import {getSiteId, populateSiteId} from "./siteid";
import {activateSuccessTracking} from "./success_tracking";

populateSiteId();
activateSuccessTracking();

const vcStylesId = "vcStyles"
if (document.querySelector(`#${vcStylesId}`) == null) {
  const style = document.createElement('style');
  style.id = vcStylesId;

  style.textContent = '[data-vc-experiment] { visibility: hidden; }';

  logDebug("Adding styles to hide unused experimental variants");
  document.head.appendChild(style);
}

type Variant = {
  variant: string;
  element: HTMLElement;
}

let observers = new Set<IntersectionObserver>();
let exposureRecorded: Record<string, boolean> = {};

function observe(element: HTMLElement, siteId: string, experimentId: string, winningId: string) {
  const observer = new IntersectionObserver((entries) => {
    void Promise.all(entries.map(async entry => {
      if (entry.isIntersecting && !exposureRecorded[experimentId]) {
        logDebug("Tracking exposure because of observer");
        await recordExposure(siteId, experimentId, winningId);
        exposureRecorded[experimentId] = true;
        observer.disconnect();
        observers.delete(observer);
      }
    })).catch(error => {
      console.error(`Failure running version compare exposure tracking: ${error}`);
    });
  }, observerOptions());
    
  observer.observe(element);
}

function stopIntersectionObservers() {
  observers.forEach((observer) => {
    observer.disconnect();
  })
  observers = new Set();
}


function runStaticExperiments(siteId: string) {
  const experimentElements = document.querySelectorAll('[data-vc-experiment][data-vc-variant]');

  const experiments: Record<string, Variant[]> = {};

  experimentElements.forEach(element => {
    let htmlElement = element as HTMLElement;

    const experiment = htmlElement.getAttribute('data-vc-experiment');
    const variant = htmlElement.getAttribute('data-vc-variant'); 

    if (experiment && variant) {
      if (!experiments[experiment]) {
        experiments[experiment] = new Array<Variant>();
      }
      const variants = experiments[experiment];
      variants.push({
        variant,
        element: htmlElement
      });
    }
  });

  let experimentRunPromises = Object.entries(experiments).map(async ([experimentId, variantElements]: [string, Variant[]]) => {
    const variants = new Set<string>();
    variantElements.forEach(({ variant }) => variants.add(variant));
    
    const variantNames = Array.from(variants.values());
    const winner = await choose(siteId, experimentId, variantNames);

    if (winner) {
      variantElements.forEach(({ variant, element }) => {
        if (variant === winner) {
          element.style.visibility = 'visible';
          observe(element, siteId, experimentId, variant);
        } else {
          element.style.display = 'none';
        }
      })
    }
  });
  
  void Promise.all(experimentRunPromises).catch(error => console.error(`Version.Compare: Failed to run experiments: ${error}`));
}

const siteId = getSiteId();
if (siteId) {
  // Run the experiments at once, once the priors have been loaded.
  awaitPriorsLoaded(siteId, () => runStaticExperiments(siteId));

  const observer = new MutationObserver(async _mutations => {
    stopIntersectionObservers();
    runStaticExperiments(siteId);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true, 
    attributes: true,
    attributeFilter: ['data-vc-experiment', 'data-vc-variant']
  });
}
